/**
 * RECOMMEND / EDIT RECOMMENDATION — the phone's bottom sheet (DESIGN-CHANGELOG
 * 2026-09-03 "Sending (second pass)"; `design/GT Mobile Mine.dc.html:466-513`).
 * AA phase 3.
 *
 * The desktop modal stacked: game block, TO + THE WHOLE PARTY, a vertical
 * roster of 48px rows (each a thumb target), the note, the hint on its own
 * line, then two equal 44px buttons — CANCEL and SEND ▸ / SAVE ▸ / REVOKE ▸.
 * Root-mounted; the safe-area inset rides the footer.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { sendModalVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Check({ on }: { on: boolean }): JSX.Element {
  return (
    <span
      style={{
        width: 16,
        height: 16,
        flex: 'none',
        boxSizing: 'border-box',
        border: '1px solid ' + (on ? 'var(--acc)' : 'var(--gt-border-2)'),
        background: on ? 'var(--acc)' : 'transparent',
        display: 'grid',
        placeItems: 'center',
        fontFamily: MONO,
        fontSize: 10,
        color: 'var(--gt-ink)',
      }}
    >
      {on ? '✓' : ''}
    </span>
  );
}

export default function SendSheet(): JSX.Element {
  const s = useQd();
  const v = sendModalVM(s);
  const art = useArt(v.id + 'w', v.hdrCands);

  return (
    <Sheet
      onClose={() => s.closeSend()}
      label="Recommend sheet"
      maxHeight="90vh"
      zIndex={85}
      header={{ title: v.title, titleClr: 'var(--gt-text)', titleFs: '9.5px', note: 'THEY SEE THE NOTE, NOT THE PARTY' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--gt-line)' }}>
        <div style={{ position: 'relative', width: 104, height: 49, flex: 'none', background: v.fall, overflow: 'hidden', border: '1px solid var(--gt-line)' }}>
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={10} ini={v.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 14, color: 'var(--gt-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.n}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.07em', color: 'var(--gt-faint)' }}>{v.dev}</span>
        </div>
      </div>

      <div style={{ padding: '12px 14px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.11em', color: 'var(--gt-faint)' }}>TO</span>
          <div style={{ flex: 1 }} />
          <div
            onClick={() => s.sdAll()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 40,
              border: '1px solid ' + (v.allOn ? 'var(--acc)' : 'var(--gt-border-2)'),
              color: v.allOn ? 'var(--acc)' : 'var(--gt-text-5)',
              background: v.allOn ? 'var(--accSoft)' : 'transparent',
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '.07em',
              padding: '0 12px',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <Check on={v.allOn} />
            THE WHOLE PARTY
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {v.rows.map((r) => (
            <div
              key={r.key}
              onClick={() => s.sdToggle(r.k)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid ' + (r.on ? 'var(--acc)' : 'var(--gt-border)'),
                background: r.on ? 'var(--accSoft)' : 'var(--gt-card)',
                padding: '0 12px',
                minHeight: 48,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <Check on={r.on} />
              <div
                style={{
                  position: 'relative',
                  width: 26,
                  height: 26,
                  flex: 'none',
                  background: r.hueBg,
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: PIXEL,
                  fontSize: 7.5,
                  color: 'var(--gt-ink)',
                  overflow: 'hidden',
                }}
              >
                {r.ini}
                {r.hasAv ? <div style={{ position: 'absolute', inset: 0, background: r.avBg }} /> : null}
              </div>
              <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '.05em', color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.name}
                </span>
                {r.hasPeek ? (
                  <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: r.peekClr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.peek}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '10px 14px 6px' }}>
        <textarea
          value={v.note}
          onChange={(e) => s.sdSetNote(e.target.value)}
          maxLength={v.noteMax}
          rows={3}
          placeholder={v.notePh}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            resize: 'none',
            background: 'var(--gt-field)',
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text)',
            fontFamily: 'var(--gt-font-sans)',
            fontSize: 14,
            lineHeight: 1.5,
            padding: '10px 11px',
            outline: 'none',
          }}
        />
        <div style={{ minHeight: 14, marginTop: 6, fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: v.isRevoke ? 'var(--acc2)' : 'var(--gt-faint)' }}>
          {v.hint}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '10px 14px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--gt-line)', background: 'var(--gt-raised)' }}>
        <div
          onClick={() => s.closeSend()}
          style={{ flex: 1, height: 44, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)', cursor: 'pointer', boxSizing: 'border-box' }}
        >
          CANCEL
        </div>
        <div
          onClick={() => (v.canSend ? void s.sdSend() : undefined)}
          style={{
            flex: 1,
            height: 44,
            display: 'grid',
            placeItems: 'center',
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: '.07em',
            color: v.isRevoke && v.canSend ? 'var(--acc2)' : v.sendClr,
            border: '1px solid ' + (v.isRevoke && v.canSend ? 'var(--acc2)' : v.sendBd),
            background: v.isRevoke && v.canSend ? 'var(--acc2Soft)' : v.sendBg,
            cursor: v.canSend ? 'pointer' : 'not-allowed',
            boxSizing: 'border-box',
          }}
        >
          {v.sendL}
        </div>
      </div>
    </Sheet>
  );
}
